import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

export interface Transaction {
  id: string;
  type: 'earn' | 'spend' | 'redeem';
  amount: number;
  description: string;
  timestamp: Date;
  gameId?: string;
  source?: string;
}

interface WalletContextType {
  balance: number;
  lifetimeEarnings: number;
  loading: boolean;
  transactions: Transaction[];
  earnCoins: (amount: number, description: string, source?: string) => Promise<void>;
  spendCoins: (amount: number, description: string) => Promise<boolean>;
  redeemCoinsForGiftCard: (amount: number, description: string, giftCardData?: any) => Promise<boolean>;
  fetchTransactions: (limitCount?: number) => Promise<void>;
  refreshBalance: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [lifetimeEarnings, setLifetimeEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Initialize or get user wallet
  const initializeWallet = async () => {
    if (!currentUser) {
      setBalance(0);
      setLoading(false);
      return;
    }

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      const walletSnap = await getDoc(walletRef);

      if (!walletSnap.exists()) {
        // Create new wallet with starting balance
        await setDoc(walletRef, {
          balance: 100, // Starting bonus
          lifetimeEarnings: 100, // Track lifetime earnings from the start
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setBalance(100);
        setLifetimeEarnings(100);
      } else {
        const walletData = walletSnap.data();
        setBalance(walletData.balance || 0);
        // Initialize lifetimeEarnings if it doesn't exist (for existing users)
        if (walletData.lifetimeEarnings === undefined) {
          // Calculate from transactions or use balance as fallback
          const transactionsRef = collection(db, 'transactions');
          const q = query(
            transactionsRef,
            where('userId', '==', currentUser.uid),
            where('type', '==', 'earn')
          );
          const transactionSnap = await getDocs(q);
          const totalEarned = transactionSnap.docs.reduce((sum, doc) => {
            const data = doc.data();
            return sum + (data.amount > 0 ? data.amount : 0);
          }, 0);
          const calculatedLifetimeEarnings = Math.max(totalEarned, walletData.balance || 0);
          await updateDoc(walletRef, { lifetimeEarnings: calculatedLifetimeEarnings });
          setLifetimeEarnings(calculatedLifetimeEarnings);
        } else {
          setLifetimeEarnings(walletData.lifetimeEarnings || 0);
        }
      }
    } catch (error) {
      console.error('Error initializing wallet:', error);
      setBalance(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (limitCount: number = 100) => {
    if (!currentUser) {
      setTransactions([]);
      return;
    }

    try {
      const transactionsRef = collection(db, 'transactions');
      const q = query(
        transactionsRef,
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);

      const fetchedTransactions: Transaction[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          amount: data.amount,
          description: data.description,
          timestamp: data.timestamp?.toDate() || new Date(),
          gameId: data.gameId,
          source: data.source,
        };
      });

      setTransactions(fetchedTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  // Refresh balance
  const refreshBalance = async () => {
    if (!currentUser) return;

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      const walletSnap = await getDoc(walletRef);
      if (walletSnap.exists()) {
        const walletData = walletSnap.data();
        setBalance(walletData.balance || 0);
        setLifetimeEarnings(walletData.lifetimeEarnings || 0);
      }
    } catch (error) {
      console.error('Error refreshing balance:', error);
    }
  };

  // Earn coins
  const earnCoins = async (amount: number, description: string, source?: string) => {
    if (!currentUser || amount <= 0) {
      console.warn('Invalid earnCoins call:', { amount, currentUser: !!currentUser });
      return;
    }

    // Validate amount is reasonable (prevent abuse)
    if (amount > 10000) {
      console.error('Suspicious coin amount:', amount);
      throw new Error('Invalid coin amount');
    }

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      
      // Update balance and lifetime earnings using atomic increment (secure - can't be hacked)
      // increment() is server-side and prevents direct balance manipulation
      await updateDoc(walletRef, {
        balance: increment(amount),
        lifetimeEarnings: increment(amount), // Track total coins ever earned (never decreases)
        updatedAt: serverTimestamp(),
      });

      // Create transaction record (immutable audit trail)
      // This creates a permanent record that can't be deleted or modified
      const transactionsRef = collection(db, 'transactions');
      await setDoc(doc(transactionsRef), {
        userId: currentUser.uid,
        type: 'earn',
        amount: amount,
        description: description,
        timestamp: serverTimestamp(),
        source: source || 'app',
      });

      // Update local state
      setBalance((prev) => prev + amount);
      setLifetimeEarnings((prev) => prev + amount); // Lifetime earnings always increase
      
      // Refresh transactions to show the new one
      await fetchTransactions();
      
      console.log('✅ Coins earned and transaction recorded:', { amount, description, source });
    } catch (error) {
      console.error('❌ Error earning coins:', error);
      throw error;
    }
  };

  // Spend coins
  const spendCoins = async (amount: number, description: string): Promise<boolean> => {
    if (!currentUser || amount <= 0) return false;

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      const walletSnap = await getDoc(walletRef);
      
      const currentBalance = walletSnap.exists() ? walletSnap.data().balance || 0 : 0;
      
      if (currentBalance < amount) {
        return false; // Insufficient balance
      }

      // Update balance
      await updateDoc(walletRef, {
        balance: increment(-amount),
        updatedAt: serverTimestamp(),
      });

      // Create transaction record
      const transactionsRef = collection(db, 'transactions');
      await setDoc(doc(transactionsRef), {
        userId: currentUser.uid,
        type: 'spend',
        amount: -amount,
        description: description,
        timestamp: serverTimestamp(),
      });

      // Update local state
      setBalance((prev) => Math.max(0, prev - amount));
      
      // Refresh transactions
      await fetchTransactions();
      
      return true;
    } catch (error) {
      console.error('Error spending coins:', error);
      return false;
    }
  };

  // Redeem coins for gift card
  const redeemCoinsForGiftCard = async (
    amount: number,
    description: string,
    giftCardData?: any
  ): Promise<boolean> => {
    if (!currentUser || amount <= 0) return false;

    try {
      const walletRef = doc(db, 'wallets', currentUser.uid);
      const walletSnap = await getDoc(walletRef);
      
      const currentBalance = walletSnap.exists() ? walletSnap.data().balance || 0 : 0;
      
      if (currentBalance < amount) {
        return false; // Insufficient balance
      }

      // Update balance (deduct coins)
      await updateDoc(walletRef, {
        balance: increment(-amount),
        updatedAt: serverTimestamp(),
      });

      // Create transaction record
      const transactionsRef = collection(db, 'transactions');
      await setDoc(doc(transactionsRef), {
        userId: currentUser.uid,
        type: 'redeem',
        amount: -amount,
        description: description,
        timestamp: serverTimestamp(),
        source: 'gift-card',
        giftCardData: giftCardData || null,
      });

      // Update local state
      setBalance((prev) => Math.max(0, prev - amount));
      
      // Refresh transactions
      await fetchTransactions();
      
      console.log('✅ Gift card redemption recorded:', { amount, description, giftCardData });
      return true;
    } catch (error) {
      console.error('❌ Error redeeming coins for gift card:', error);
      return false;
    }
  };

  // Initialize wallet when user changes
  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      if (currentUser) {
        try {
          await initializeWallet();
          await fetchTransactions();
        } catch (error) {
          console.error('Error initializing wallet context:', error);
          if (isMounted) {
            setBalance(0);
            setLoading(false);
          }
        }
      } else {
        if (isMounted) {
          setBalance(0);
          setTransactions([]);
          setLoading(false);
        }
      }
    };
    
    init();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const value: WalletContextType = {
    balance,
    lifetimeEarnings,
    loading,
    transactions,
    earnCoins,
    spendCoins,
    redeemCoinsForGiftCard,
    fetchTransactions,
    refreshBalance,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

